import { MouseEvent } from "react";
import { Box, Typography, Stack, Button } from "@mui/material";
import { grey, red } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import { RosterPlayer } from "../types";
import { MINUTES, PLAYER_SLOTS } from "../constants";
import { getPlayerForCell, getInitials } from "./utils";

export default function QuarterGrid({
  quarter,
  slotAssignments,
  roster,
  playerColorMap,
  onCellClick,
  onCarryOver,
  onClear,
}: {
  quarter: string;
  slotAssignments: Record<string, number | null>;
  roster: RosterPlayer[];
  playerColorMap: Record<number, string>;
  onCellClick: (
    event: MouseEvent<HTMLElement>,
    quarter: string,
    minute: number,
    slot: number
  ) => void;
  onCarryOver?: () => void;
  onClear?: () => void;
}) {
  const theme = useTheme();
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
          {quarter}
        </Typography>
        {onCarryOver && (
          <Button
            size="small"
            variant="outlined"
            onClick={onCarryOver}
            sx={{
              py: 0,
              minHeight: 20,
              fontSize: 10,
              "@media print": { display: "none" },
            }}
          >
            Fill
          </Button>
        )}
        {onClear && (
          <Button
            size="small"
            variant="outlined"
            onClick={onClear}
            sx={{
              py: 0,
              minHeight: 20,
              fontSize: 10,
              color: red[400],
              borderColor: red[400],
              "@media print": { display: "none" },
            }}
          >
            Clear
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 48px)",
          gap: 0.5,
        }}
      >
        {MINUTES.map((minute) => (
          <Stack
            key={minute}
            alignItems="center"
            justifyContent="center"
            sx={{
              height: 24,
              bgcolor: grey[300],
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", fontSize: 11 }}
            >
              {minute}:00
            </Typography>
          </Stack>
        ))}
        {PLAYER_SLOTS.map((slot) =>
          MINUTES.map((minute) => {
            const player = getPlayerForCell(
              quarter,
              minute,
              slot,
              slotAssignments,
              roster
            );
            const playerColor = player
              ? playerColorMap[player.playerId]
              : undefined;

            return (
              <Box
                key={`${slot}-${minute}`}
                onClick={(e) => onCellClick(e, quarter, minute, slot)}
                sx={{
                  height: 28,
                  bgcolor: playerColor ?? grey[100],
                  color: playerColor
                    ? theme.palette.getContrastText(playerColor)
                    : "inherit",
                  border: 1,
                  borderColor: playerColor ?? grey[300],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: "bold",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: grey[200],
                  },
                }}
              >
                {player ? getInitials(player.playerName) : ""}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
